import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { useWireAdminApi } from "../../../../common/hooks/use-wire-admin-api.hook";

export const MAX_PDF_BYTES = 10 * 1024 * 1024;

const labelClass = "mb-1.5 text-xs font-medium text-muted-foreground";
const today = () => new Date().toISOString().slice(0, 10);

interface AddPastInvoiceDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    vendorId: string;
    /** Pre-selected currency (the vendor's plan currency). */
    defaultCurrency?: string;
    onAdded: () => void;
}

/**
 * Record an invoice billed before plans existed, with its original PDF, so the
 * vendor finds it in their billing history. It is stored as already paid and
 * changes nothing about the vendor's plan or renewals.
 */
export const AddPastInvoiceDialog = ({ open, onOpenChange, vendorId, defaultCurrency, onAdded }: AddPastInvoiceDialogProps) => {
    const { t } = useTranslation();
    const wireAdminApi = useWireAdminApi();
    const [file, setFile] = useState<File | null>(null);
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const [amount, setAmount] = useState("");
    const [currency, setCurrency] = useState((defaultCurrency || "eur").toUpperCase());
    const [interval, setInterval] = useState<"month" | "year">("month");
    const [issuedAt, setIssuedAt] = useState("");
    const [paidAt, setPaidAt] = useState("");
    const [periodStart, setPeriodStart] = useState("");
    const [periodEnd, setPeriodEnd] = useState("");
    const [reference, setReference] = useState("");
    const [saving, setSaving] = useState(false);

    // The first thing wrong with the form, said in words; empty when it is ready.
    const problem = (() => {
        if (file && !(file.type === "application/pdf" || /\.pdf$/i.test(file.name))) return t("ADMIN_VENDOREDIT.INVOICES.ERR_FILE_TYPE");
        if (file && file.size > MAX_PDF_BYTES) return t("ADMIN_VENDOREDIT.INVOICES.ERR_FILE_SIZE");
        if (periodStart && periodEnd && periodEnd <= periodStart) return t("ADMIN_VENDOREDIT.INVOICES.ERR_PERIOD");
        if ((issuedAt && issuedAt > today()) || (paidAt && paidAt > today())) return t("ADMIN_VENDOREDIT.INVOICES.ERR_FUTURE");
        return "";
    })();

    const amountValue = Number(amount);
    const ready =
        !!file && !problem && invoiceNumber.trim() !== "" && Number.isFinite(amountValue) && amountValue > 0
        && /^[A-Za-z]{3}$/.test(currency.trim()) && !!issuedAt && !!paidAt && !!periodStart && !!periodEnd;

    const submit = async () => {
        if (!file || !ready) return;
        setSaving(true);
        try {
            await wireAdminApi.addHistoricalInvoice(vendorId, file, {
                invoiceNumber: invoiceNumber.trim(),
                amount: amountValue,
                currency: currency.trim().toLowerCase(),
                interval,
                periodStart,
                periodEnd,
                issuedAt,
                paidAt,
                ...(reference.trim() ? { reference: reference.trim() } : {}),
            });
            toast.success(t("ADMIN_VENDOREDIT.INVOICES.SAVED"));
            // Ready for the next one: keep the currency and interval, clear the rest.
            setFile(null); setInvoiceNumber(""); setAmount(""); setIssuedAt(""); setPaidAt("");
            setPeriodStart(""); setPeriodEnd(""); setReference("");
            onAdded();
            onOpenChange(false);
        } catch (err: any) {
            // The server says exactly what is wrong (a number already used, not a PDF…).
            toast.error(err?.response?.data?.message || t("ADMIN_VENDOREDIT.INVOICES.FAILED"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
            <DialogContent className="sm:max-w-xl" data-testid="add-past-invoice-dialog">
                <DialogHeader>
                    <DialogTitle>{t("ADMIN_VENDOREDIT.INVOICES.DIALOG_TITLE")}</DialogTitle>
                    <DialogDescription>{t("ADMIN_VENDOREDIT.INVOICES.DIALOG_BODY")}</DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="md:col-span-2">
                        <Label htmlFor="past-invoice-file" className={labelClass}>{t("ADMIN_VENDOREDIT.INVOICES.FILE")}</Label>
                        <Input
                            id="past-invoice-file"
                            type="file"
                            accept="application/pdf,.pdf"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />
                        <p className="mt-1.5 text-xs text-muted-foreground">{t("ADMIN_VENDOREDIT.INVOICES.FILE_HINT")}</p>
                    </div>
                    <div>
                        <Label htmlFor="past-invoice-number" className={labelClass}>{t("ADMIN_VENDOREDIT.INVOICES.NUMBER")}</Label>
                        <Input id="past-invoice-number" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
                    </div>
                    <div className="grid grid-cols-[1fr_6rem] gap-2">
                        <div>
                            <Label htmlFor="past-invoice-amount" className={labelClass}>{t("ADMIN_VENDOREDIT.INVOICES.AMOUNT")}</Label>
                            <Input id="past-invoice-amount" type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
                        </div>
                        <div>
                            <Label htmlFor="past-invoice-currency" className={labelClass}>{t("ADMIN_VENDOREDIT.INVOICES.CURRENCY")}</Label>
                            <Input id="past-invoice-currency" maxLength={3} value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="past-invoice-interval" className={labelClass}>{t("ADMIN_VENDOREDIT.INVOICES.INTERVAL")}</Label>
                        <Select value={interval} onValueChange={(v) => setInterval(v as "month" | "year")}>
                            <SelectTrigger id="past-invoice-interval" className="w-full">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="month">{t("ADMIN_VENDOREDIT.INVOICES.MONTHLY")}</SelectItem>
                                <SelectItem value="year">{t("ADMIN_VENDOREDIT.INVOICES.YEARLY")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="past-invoice-reference" className={labelClass}>{t("ADMIN_VENDOREDIT.INVOICES.REFERENCE")}</Label>
                        <Input
                            id="past-invoice-reference"
                            placeholder={t("ADMIN_VENDOREDIT.INVOICES.REFERENCE_PLACEHOLDER")}
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                        />
                    </div>
                    <div>
                        <Label htmlFor="past-invoice-issued" className={labelClass}>{t("ADMIN_VENDOREDIT.INVOICES.ISSUED_AT")}</Label>
                        <Input id="past-invoice-issued" type="date" max={today()} value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
                    </div>
                    <div>
                        <Label htmlFor="past-invoice-paid" className={labelClass}>{t("ADMIN_VENDOREDIT.INVOICES.PAID_AT")}</Label>
                        <Input id="past-invoice-paid" type="date" max={today()} value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
                    </div>
                    <div>
                        <Label htmlFor="past-invoice-period-start" className={labelClass}>{t("ADMIN_VENDOREDIT.INVOICES.PERIOD_START")}</Label>
                        <Input id="past-invoice-period-start" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
                    </div>
                    <div>
                        <Label htmlFor="past-invoice-period-end" className={labelClass}>{t("ADMIN_VENDOREDIT.INVOICES.PERIOD_END")}</Label>
                        <Input id="past-invoice-period-end" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
                    </div>
                </div>

                {problem && <p role="alert" className="text-sm text-destructive">{problem}</p>}

                <DialogFooter>
                    <Button variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
                        {t("ADMIN_VENDOREDIT.INVOICES.CANCEL")}
                    </Button>
                    <Button disabled={!ready || saving} onClick={submit}>
                        {t("ADMIN_VENDOREDIT.INVOICES.SUBMIT")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
